import { Component, OnInit } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';

import { filter, map, take } from 'rxjs/operators';
import { CalendarConfigService } from '../calendar/calendar-config.service';
import { FirstRunCalloutsService } from '../first-run/first-run-callouts/first-run-callouts.service';
import { FirstRunConfigService } from '../first-run/first-run-component/first-run-config.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { MruConfigService } from '../shared/mru/mru-config.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { EventService } from '../shared/services/event.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';

@Component({
    selector: 'ohp-home',
    templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit {
    public showTilesSection: boolean;
    public showCalendar: boolean;
    public showPlaces: boolean;
    public bottomTrayEnabled: boolean;
    public showEmptyNavState: boolean;
    public leftNavEmptyStateString: string;
    public leftNavEmptyImage: string;
    public leftNavEmptyStateSubtitle: string;
    public showSuggestedActions: boolean;

    private readonly coreWorkloadIds: Array<string> = ['ShellWordOnline', 'ShellExcelOnline', 'ShellPowerPointOnline', 'ShellMail'];

    constructor(
        private readonly calendarConfigService: CalendarConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly eventService: EventService,
        private readonly firstRunConfigService: FirstRunConfigService,
        private readonly firstRunCalloutsService: FirstRunCalloutsService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly mruConfigService: MruConfigService,
        private readonly userContextService: UserContextService,
        private readonly workloadsService: WorkloadsService,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) { }

    public ngOnInit() {
        const capabilities = this.capabilitiesService.capabilities;
        const isConsumer = this.clientStartupConfigService.config.authType === AuthType.MSA;
        this.showSuggestedActions = capabilities.suggestedActionsEnabled;
        this.showTilesSection = !capabilities.appsInHero && !capabilities.leftNavReact;
        this.showCalendar = !capabilities.edgeworthEnabled && capabilities.calendarEnabled && !!this.calendarConfigService.config;
        this.bottomTrayEnabled = capabilities.bottomTrayEnabled;
        this.showPlaces = this.clientStartupConfigService.config.authType === AuthType.OrgId && !capabilities.edgeworthEnabled;
        this.leftNavEmptyImage = capabilities.leftNavEmptyImage;
        this.leftNavEmptyStateString = this.locService.LeftNavNonWXPEmptyStateString;
        this.leftNavEmptyStateSubtitle = this.locService.LeftNavNonWXPEmptyStateSubtitleString;

        Promise.all([this.firstRunConfigService.getConfig(), this.consumerInstallConfigService.getConfig()]).then(([firstRunConfig, consumerInstallConfig]) => {
            const subscriptionStatus = consumerInstallConfig && consumerInstallConfig.currentSkuInfo ? consumerInstallConfig.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
            const isPaidConsumer = !(subscriptionStatus === ConsumerSkuType.NonActive || subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NotDetected);
            if (( (!firstRunConfig || !firstRunConfig.model || capabilities.modalFreCalloutEnabled || isPaidConsumer)
                || this.userContextService.user.isEduFaculty
                || (capabilities.eduPageTourFreEnabled && this.userContextService.user.isEdu))
                // callouts on FREs for edu faculty, all edu on page tour flight, paid consumers, and when modalFreCalout is enabled (currently for all except edu)
                && !this.userContextService.user.hasNoLicense) {
                this.firstRunCalloutsService.initializeFirstRun();
            }

            if (isConsumer) {
                const params: InstrumentationParams = {};
                params[InstrumentationParam.SkuType] = ConsumerSkuType[subscriptionStatus];
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.ConsumerSkuLoaded, params);
                logFeature({
                    featureName: KnownFeatureNames.Upsell,
                    eventName: KnownInstrumentationEvents.ConsumerSkuLoaded,
                    area: 'Other',
                    params: {
                        SkuType: ConsumerSkuType[subscriptionStatus]
                    }
                });
            }
        });
        let recommendedPromise = Promise.resolve(false);
        const mruConfigServiceConfig = this.mruConfigService.config;
        if (mruConfigServiceConfig.recommendedEnabled) {
            recommendedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.recommendedDataReady),
                map(event => event.args[0] > 0),
                take(1))
                .toPromise();
        }
        let mruPromise = undefined;
        if (mruConfigServiceConfig.showZeroExperience) { //contingent on having OneDrive tile
            mruPromise = Promise.resolve(true);
            // if we show zero experience then we won't have a blank page
            // there are other cases we have to check (must have either create menu, upload, or orgId) but will deal with them later
        } else {
            mruPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.mruDataReady),
                map(event => event.args[0] > 0),
                take(1))
                .toPromise();
        }
        let calendarPromise = Promise.resolve(false);
        if (this.showCalendar) {
            calendarPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.calendarDataReady),
                map(event => event.args[0] > 0),
                take(1))
                .toPromise();
        }

        let announcementPromise = Promise.resolve(false);
        announcementPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.announcementsReady),
                map(event => event.args[0] > 0),
                take(1))
                .toPromise();

        // TODO: replace with an event listener after pinning questions are resolved
        const workloadsContainsCoreAppsPromise = this.workloadsService.getWorkloads().then(workloads => {
            const coreWorkloads = workloads.filter(workload => this.coreWorkloadIds.indexOf(workload.Id) >= 0);
            return coreWorkloads.length > 0;
        });

        Promise.all([recommendedPromise, mruPromise, workloadsContainsCoreAppsPromise, calendarPromise, announcementPromise]).then(
            ([recommendedSeen, mruSeen, hasCoreApps, calendarSeen, hasAnnouncements]) => {
                if (!(recommendedSeen || mruSeen || hasCoreApps || calendarSeen || hasAnnouncements)) {
                    // show empty state
                    // TODO: may have more edge cases
                    this.showEmptyNavState = true;
            }
        });
    }
}
